import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Divider,
  Empty,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import StageStepper from "../workflow/StageStepper";
import MeetingScheduler from "../workflow/MeetingScheduler";
import ScoringChart from "../workflow/ScoringChart";
import BriefViewer from "../workflow/BriefViewer";
import DecisionPanel from "../workflow/DecisionPanel";
import VendorMatchingStage from "../workflow/VendorMatchingStage";
import AdvisoryBoardNotes from "../workflow/AdvisoryBoardNotes";
import LeadershipBriefSent from "../workflow/LeadershipBriefSent";
import RiskAssessmentPanel from "../workflow/RiskAssessmentPanel";
import {
  advanceWorkflowStage,
  createWorkflow,
  getWorkflow,
} from "../../lib/api";
import type {
  WorkflowBundle,
  WorkflowStage,
  WorkflowStageId,
} from "./types";

interface Props {
  intakeId: string;
}

const STAGE_ORDER: WorkflowStageId[] = [
  "schedule_meeting",
  "scoring_chart",
  "vendor_matching",
  "ai_advisory_board_presentation",
  "leadership_presentation",
  "approval_decision",
];

/** Stages that don't gate Advance (no extra evidence required). */
const UNGATED_STAGES: WorkflowStageId[] = ["schedule_meeting", "approval_decision"];

interface AdvanceGateResult {
  canAdvance: boolean;
  reason?: string;
}

function evaluateAdvanceGate(
  currentStage: WorkflowStageId,
  stage: WorkflowStage | undefined,
  bundle: WorkflowBundle,
): AdvanceGateResult {
  if (UNGATED_STAGES.includes(currentStage)) {
    return { canAdvance: true };
  }
  if (currentStage === "scoring_chart") {
    if (!bundle.evaluation) {
      return {
        canAdvance: false,
        reason: "Submit the scoring evaluation before advancing past Fill Scoring Chart.",
      };
    }
    if (!stage?.riskAssessment?.riskLevel) {
      return {
        canAdvance: false,
        reason: "Complete the Risk Assessment before advancing past Fill Scoring Chart.",
      };
    }
    return { canAdvance: true };
  }
  if (currentStage === "vendor_matching") {
    if (!bundle.workflow?.selectedVendor) {
      return {
        canAdvance: false,
        reason: "Select a vendor (or 'Build internally') before advancing.",
      };
    }
    return { canAdvance: true };
  }
  if (currentStage === "ai_advisory_board_presentation") {
    if (!stage?.notes || !stage.notes.trim()) {
      return {
        canAdvance: false,
        reason: "Add AI Advisory Board meeting notes before advancing.",
      };
    }
    return { canAdvance: true };
  }
  if (currentStage === "leadership_presentation") {
    if (!stage?.briefSentToLeadership) {
      return {
        canAdvance: false,
        reason: "Confirm the brief was sent to leadership before advancing.",
      };
    }
    return { canAdvance: true };
  }
  return { canAdvance: true };
}

export default function WorkflowTab({ intakeId }: Props) {
  const { message } = App.useApp();
  const [bundle, setBundle] = useState<WorkflowBundle | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeStage, setActiveStage] = useState<WorkflowStageId>("schedule_meeting");
  const [busy, setBusy] = useState(false);
  // Track whether we've already attempted the silent auto-create so a
  // persistent failure doesn't loop refetch → create → refetch indefinitely.
  const autoCreateAttemptedRef = useRef(false);
  const [autoCreateFailed, setAutoCreateFailed] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getWorkflow(intakeId);
      setBundle(data);
      const current = data?.workflow?.currentStage as WorkflowStageId | undefined;
      if (current && current !== "completed") {
        setActiveStage(current);
      } else if (current === "completed") {
        setActiveStage("approval_decision");
      }
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [intakeId, message]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Silent auto-create: if the intake exists but has no workflow yet (e.g. a
  // backfill case predating the auto-start in agency_intake.py), kick off a
  // create + refetch once. We deliberately do NOT show the manual "Start
  // workflow" CTA up front — only as a fallback if the silent create errors.
  useEffect(() => {
    if (loading) return;
    if (!bundle) return; // initial fetch hasn't returned yet
    if (bundle.workflow) return;
    if (autoCreateAttemptedRef.current) return;
    autoCreateAttemptedRef.current = true;
    (async () => {
      try {
        await createWorkflow(intakeId);
        await refresh();
      } catch (error) {
        setAutoCreateFailed(true);
        if (error instanceof Error) message.error(error.message);
      }
    })();
  }, [bundle, loading, intakeId, refresh, message]);

  const handleCreate = async () => {
    try {
      setBusy(true);
      await createWorkflow(intakeId);
      message.success("Workflow created");
      setAutoCreateFailed(false);
      await refresh();
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleAdvance = async () => {
    if (!bundle?.workflow) return;
    try {
      setBusy(true);
      await advanceWorkflowStage(bundle.workflow.id);
      message.success("Stage advanced");
      await refresh();
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  if (!bundle?.workflow) {
    // While the silent auto-create is in flight, show the loading state — only
    // surface the manual CTA once we've definitively given up (autoCreateFailed)
    // or while the very first fetch is still pending.
    const showManualCta = autoCreateFailed && !loading;
    return (
      <Card>
        <Empty
          description={
            loading || (!autoCreateFailed && bundle === null)
              ? "Loading workflow…"
              : autoCreateFailed
                ? "Could not start the workflow automatically. Try again?"
                : "Starting workflow…"
          }
        >
          {showManualCta ? (
            <Button type="primary" onClick={handleCreate} loading={busy}>
              Start workflow
            </Button>
          ) : null}
        </Empty>
      </Card>
    );
  }

  const workflow = bundle.workflow;
  const isCompleted = workflow.currentStage === "completed";
  const currentStageObj = workflow.stages.find((s) => s.id === workflow.currentStage);
  const advanceGate = evaluateAdvanceGate(workflow.currentStage, currentStageObj, bundle);
  const canAdvanceFromHere =
    activeStage === workflow.currentStage && !isCompleted && activeStage !== "approval_decision";
  // Find the per-stage objects we need for the new editor panels.
  const scoringStage = workflow.stages.find((s) => s.id === "scoring_chart");
  const advisoryStage = workflow.stages.find(
    (s) => s.id === "ai_advisory_board_presentation",
  );
  const leadershipStage = workflow.stages.find(
    (s) => s.id === "leadership_presentation",
  );

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <StageStepper
            workflow={workflow}
            activeStage={activeStage}
            onStageClick={(stageId) => setActiveStage(stageId)}
          />
          <Space wrap>
            {canAdvanceFromHere ? (
              advanceGate.canAdvance ? (
                <Button onClick={handleAdvance} loading={busy} type="primary">
                  Advance to next stage
                </Button>
              ) : (
                <Tooltip title={advanceGate.reason}>
                  {/* span wrapper so the disabled Button's pointer-events:none
                      doesn't swallow the Tooltip hover. */}
                  <span>
                    <Button disabled type="primary">
                      Advance to next stage
                    </Button>
                  </span>
                </Tooltip>
              )
            ) : null}
            <Button onClick={refresh} loading={loading}>
              Refresh
            </Button>
          </Space>
        </Space>
      </Card>

      <Divider style={{ margin: 0 }} />

      {activeStage === "schedule_meeting" ? (
        <MeetingScheduler
          intakeId={intakeId}
          workflowId={workflow.id}
          meetings={bundle.meetings}
          onChanged={refresh}
        />
      ) : null}

      {activeStage === "scoring_chart" ? (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <ScoringChart
            intakeId={intakeId}
            rubric={bundle.rubric}
            aggregate={bundle.aggregate}
            evaluation={bundle.evaluation}
            onSubmitted={refresh}
          />
          {scoringStage ? (
            <RiskAssessmentPanel
              workflowId={workflow.id}
              stage={scoringStage}
              onSaved={refresh}
            />
          ) : null}
        </Space>
      ) : null}

      {activeStage === "vendor_matching" ? (
        <VendorMatchingStage
          intakeId={intakeId}
          workflow={workflow}
          onSelectionSaved={refresh}
        />
      ) : null}

      {activeStage === "ai_advisory_board_presentation" ? (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <BriefViewer
            intakeId={intakeId}
            briefType="ai_advisory_board"
            briefs={bundle.briefs}
            onGenerated={refresh}
          />
          {advisoryStage ? (
            <AdvisoryBoardNotes
              workflowId={workflow.id}
              stage={advisoryStage}
              onSaved={refresh}
            />
          ) : null}
        </Space>
      ) : null}

      {activeStage === "leadership_presentation" ? (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <BriefViewer
            intakeId={intakeId}
            briefType="leadership"
            briefs={bundle.briefs}
            onGenerated={refresh}
          />
          {leadershipStage ? (
            <LeadershipBriefSent
              workflowId={workflow.id}
              stage={leadershipStage}
              onSaved={refresh}
            />
          ) : null}
        </Space>
      ) : null}

      {activeStage === "approval_decision" ? (
        <DecisionPanel workflow={workflow} onRecorded={refresh} />
      ) : null}

      {STAGE_ORDER.indexOf(activeStage) < STAGE_ORDER.indexOf(workflow.currentStage) ? (
        <Alert
          type="info"
          showIcon
          message="You are viewing a previously completed stage."
        />
      ) : null}

      {isCompleted ? (
        <Alert
          type="success"
          showIcon
          message="Workflow completed"
          description={
            workflow.decision
              ? `${workflow.decision.decision.toUpperCase()} — ${workflow.decision.rationale}`
              : "All stages completed."
          }
        />
      ) : null}

      <Card size="small" title="Audit log" extra={<Typography.Text type="secondary">most recent first</Typography.Text>}>
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          {workflow.decision ? (
            <div>
              <Tag>Decision</Tag>{" "}
              {workflow.decision.decision} — {workflow.decision.decidedAt}
            </div>
          ) : null}
          {[...workflow.stages]
            .reverse()
            .filter((stage) => stage.completedAt)
            .map((stage) => (
              <div key={`${stage.id}-${stage.completedAt}`}>
                <Tag color="success">Stage</Tag> {stage.name} completed {stage.completedAt}
              </div>
            ))}
          {bundle.briefs.map((brief) => (
            <div key={brief.id}>
              <Tag color="blue">Brief</Tag> {brief.briefType} generated {brief.generatedAt}
            </div>
          ))}
          {bundle.evaluation ? (
            <div>
              <Tag color="purple">Score</Tag>{" "}
              {bundle.evaluation.evaluatorName ||
                bundle.evaluation.evaluatorEmail ||
                bundle.evaluation.evaluatorUid}{" "}
              submitted {Object.keys(bundle.evaluation.scores || {}).length} questions{" "}
              {bundle.evaluation.submittedAt}
            </div>
          ) : null}
        </Space>
      </Card>
    </Space>
  );
}
